// ============================================================================
// CONFIG LIST EDITOR — multi-value list field for is_multi_value definitions
// ============================================================================

import React, { useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ConfigListItem } from 'api/types/config.types';

interface ConfigListEditorProps {
  values: ConfigListItem[];
  onChange: (next: ConfigListItem[]) => void;
  disabled?: boolean;
}

const ConfigListEditor: React.FC<ConfigListEditorProps> = ({ values, onChange, disabled }) => {
  const updateAt = useCallback((idx: number, patch: Partial<ConfigListItem>) => {
    const next = values.map((v, i) => (i === idx ? { ...v, ...patch } : v));
    onChange(next);
  }, [values, onChange]);

  const removeAt = useCallback((idx: number) => {
    onChange(values.filter((_, i) => i !== idx));
  }, [values, onChange]);

  const moveUp = useCallback((idx: number) => {
    if (idx === 0) return;
    const next = [...values];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onChange(next);
  }, [values, onChange]);

  const moveDown = useCallback((idx: number) => {
    if (idx === values.length - 1) return;
    const next = [...values];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    onChange(next);
  }, [values, onChange]);

  const addItem = useCallback(() => {
    onChange([...values, { value: '', isActive: true }]);
  }, [values, onChange]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
      {values.length === 0 && (
        <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
          No items. Click <b>Add</b> to create one.
        </div>
      )}
      {values.map((item, idx) => (
        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
          <input
            type="text"
            className="form-input"
            value={item.value}
            disabled={disabled}
            onChange={(e) => updateAt(idx, { value: e.target.value })}
            style={{ flex: 1, fontSize: '0.875rem' }}
          />
          <label style={{
            display: 'flex', alignItems: 'center', gap: '0.25rem',
            fontSize: '0.75rem', color: 'var(--text-secondary)', cursor: disabled ? 'default' : 'pointer',
          }}>
            <input
              type="checkbox"
              checked={item.isActive}
              disabled={disabled}
              onChange={(e) => updateAt(idx, { isActive: e.target.checked })}
            />
            Active
          </label>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => moveUp(idx)}
            disabled={disabled || idx === 0}
            style={{ padding: '0.25rem 0.4rem' }}
            title="Move up"
          >
            <IconSystem.chevronUp size={12} />
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => moveDown(idx)}
            disabled={disabled || idx === values.length - 1}
            style={{ padding: '0.25rem 0.4rem' }}
            title="Move down"
          >
            <IconSystem.chevronDown size={12} />
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => removeAt(idx)}
            disabled={disabled}
            style={{ padding: '0.25rem 0.4rem', color: '#dc2626' }}
            title="Remove"
          >
            <IconSystem.trash size={12} />
          </button>
        </div>
      ))}
      <button
        type="button"
        className="btn btn-secondary"
        onClick={addItem}
        disabled={disabled}
        style={{ alignSelf: 'flex-start', fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
      >
        <IconSystem.add size={14} />&nbsp;Add
      </button>
    </div>
  );
};

export default ConfigListEditor;
